<template>
  <v-card ref="content" class="card-wrap">
    <v-card-title>
      {{ options.title }}
      <div class="btn-right">
        <v-btn-toggle dense group>
          <!--Zoom On/Off-->
          <zoom v-if="zoomUse" :id="options.id" :active="zoomActive" />
          <!-- user's selected task-type -->
          <task-type v-if="taskTypeUse" :options="options" />
          <!--chart 설정-->
          <settings v-if="settingsChartUse" :options="options" />
          <!--grid 설정-->
          <field-selector v-if="settingsGridUse" :code="gridCode" :selected="gridSelectedFields" :store-name="options.id" />
        </v-btn-toggle>
      </div>
    </v-card-title>
    <div class="box">
      <slot name="content" :props="{ motlet: motletState }" />
    </div>
    <slot name="menu" :props="{ motlet: motletState }" />
  </v-card>
</template>

<script>
import { mapState } from 'vuex';
import { merge } from 'lodash';
import bus from '../../utils/bus';
import motletable from '../../mixins/motletable';
import settings from './settings';
import taskType from './taskType';
import FieldSelector from './settings/FieldSelector';
import Zoom from './settings/Zoom';

/*
  Commented.by.KWShin.2024.01.12.
  1. Motelet.vue는 created() 시에 register() 메소드를 이용하여
      부모로부터 받은 Props 'options'를 Vuex store에 저장한다.
  2. Store에 저장할 때 @/store/modules/common/motlet.js에 있는 mutation 'common/motlet/setMotlet'를 이용한다.
      motlet.js는 위에서 import한 @/mixins/motletable.js 모듈에서 import 한다.
  3. 변수 'motletState'는 motletable.js 모듈에 computed()로 선언되어 있으며,
      store에서 저장된 값을 꺼내 온 것이다.
 */
export default {
  mixins: [motletable],
  components: {
    settings,
    taskType,
    FieldSelector,
    Zoom,
  },
  props: {
    dense: [Boolean],
    options: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState('common/user', ['username']),
    ...mapState('common/properties', ['partialTaskType']),
    zoomUse() {
      return this.motletState.zoom?.use;
    },
    taskTypeUse() {
      return this.motletState.taskType?.use;
    },
    settingsChartUse() {
      return this.motletState.settings?.chart?.use;
    },
    settingsGridUse() {
      return this.motletState.settings?.grid?.use;
    },
  },
  watch: {
    'options.taskType.selected': {
      handler(value) {
        this.setTaskType(value);
      },
    },
    // BEGIN::Added.by.KWShin.2024.08.31.
    // 오.. 이런. 모틀릿은 options.descendant 값의 변화가 반영이 안되게 되어 있네?
    // 반영해주자...
    'options.descendant': {
      deep: true,
      handler() {
        this.$store.dispatch('common/motlet/setDescendants', { id: this.motletId, descendants: this.getDescendants() });
        this.$store.dispatch('common/motlet/setDescendant', { id: this.motletId, descendant: this.getDescendant() });
      },
    },
    // FINISH::Added.by.KWShin.2024.08.31.
  },
  methods: {
    register() {
      this.$store.dispatch('common/motlet/setMotlet', this.options);
      this.$store.dispatch('common/motlet/setDescendants', { id: this.motletId, descendants: this.getDescendants() });
      this.$store.dispatch('common/motlet/setDescendant', { id: this.motletId, descendant: this.getDescendant() });
      const monitor = merge({}, this.getMonitor(), this.options.monitor);
      this.$store.dispatch('common/motlet/setMonitor', { id: this.motletId, monitor });

      // response 내에서 지표로 사용할 field 값을 도출한다.
      // 기준 1: 임의로 지정된 field name 을 사용
      // 기준 2: motletCode 에 해당하는 filed 중에서 첫번째를 사용
      const chartValueField = this.monitor.valueField ? this.monitor.valueField : this.chartFields.length ? this.chartFields[0].code : '';
      this.setChartField(chartValueField);
      this.setGridSelectedFields(this.gridFields.filter(f => f.display).map(v => v.value));
      bus.$on(`change:selected-fields-${this.motletId}`, param => {
        this.setGridSelectedFields(param.fields);
      });
    },
    setZoomActive(active) {
      this.$store.dispatch('common/motlet/setZoomActive', { id: this.options.id, active });
    },
    getDescendants() {
      const systemId = this.options.descendant?.systemId;
      return this.$store.getters['common/system/getDescendantsById'](systemId);
    },
    getDescendant() {
      const id = this.options.descendant?.id;
      return (this.descendants || []).find(descendant => descendant.id == id);
    },
    getMonitor() {
      const systemId = this.options.monitor?.systemId;
      const monitorCode = this.options.monitor?.monitorCode;
      return this.$store.getters['common/monitor/getMonitor'](systemId, monitorCode);
    },
    setDense() {
      const target = this.$refs.content.$el.querySelector('div.box div.card-wrap div');
      target?.setAttribute('style', 'padding: 0px !important; margin: 0px !important;');
    },
  },
  mounted() {
    if (this.dense) this.setDense();
  },
  created() {
    this.register();
  },
  destroyed() {
    this.$store.dispatch('common/motlet/resetMotlet', this.options);
    bus.$off(`change:selected-fields-${this.motletId}`);
  },
};
</script>

<style scoped>
.v-application .pa-1 {
  padding: 0px !important;
}
.v-application .my-2 {
  margin-top: 0px !important;
  margin-bottom: 0px !important;
}
</style>
